import * as React from 'react'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { Violation } from '@/app/types/creator'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import { getViolationName } from '@/app/services/violation/violationNameResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useViolationCalculationTask } from '@/modules/shared/hooks/useViolationCalculationTask'
import { FaIcon } from '@/lib/fa'
import {
  getViolationWeightIcon,
  ViolationWeightType,
} from '../../hooks/useViolationsIcon'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import { formatTimespan } from '@/app/common/dateHelper'
import useDriverActivityService from '@/modules/drivers/services/DriverActivityService'
import { ActivityInfo } from '@/app/models/activityContainerModel'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { ControlledMenu, MenuItem, MenuDivider } from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'
import {
  faTriangleExclamationSolid,
  faRefreshSolid,
  faCalendarWeekSolid,
  faCalendarDaysSolid,
  faPrintSolid,
  faChartBulletSolid,
  faNoteSolid,
  faFileCertificateSolid,
} from '@/lib/fa'
import useViolationService from '@/modules/drivers/services/ViolationService'
import usePrintViolations from '../../hooks/usePrintViolations'
import NoteModal from '@/modules/drivers/tabs/activities/components/charts/yearly/note/NoteModal'
import { DayType } from '@/app/constants/dayType'
import CertificateModal from '@/modules/drivers/tabs/activities/modals/CertificateModal'
import Timeline from '@/modules/drivers/tabs/activities/components_new/Timeline'
import { TimelineProps } from '@/modules/drivers/tabs/activities/components_new/timelineProps'
import { SCENE_HEIGHT } from '@/modules/drivers/tabs/activities/components_new/utils/layout'

export const ViolationDetailsRowItem = ({
  violation,
  selectedIds,
  driverId,
  printed,
  onPrintedChange,
}: {
  violation: Violation | any
  selectedIds: number[]
  driverId: number
  printed: boolean
  onPrintedChange: (printed: boolean) => void
}) => {
  const { calculateViolations } = useViolationService()
  const { formatDateWithTime } = useFormatDate()
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const { getDriverActivity } = useDriverActivityService()
  const { t } = useLocalize()
  const { handlePrintInstruction } = usePrintViolations()

  const startCalculation = React.useCallback(
    (requestId: string) => calculateViolations({ companyId, id: requestId }),
    [calculateViolations, companyId],
  )
  const { calculate: handleCalculateViolations, scopeBusy: violationsBusy } =
    useViolationCalculationTask({ companyId, start: startCalculation })

  const [isHovered, setIsHovered] = React.useState(false)
  const [isChartExpanded, setIsChartExpanded] = React.useState(false)
  const [chartActivities, setChartActivities] =
    React.useState<ActivityInfo | null>(null)
  const [isChartLoading, setIsChartLoading] = React.useState(false)
  const [vehicleTachographMode, setVehicleTachographMode] =
    React.useState(false)
  const [isNoteModalOpen, setIsNoteModalOpen] = React.useState(false)
  const [isCertificateModalOpen, setIsCertificateModalOpen] =
    React.useState(false)
  const chartWrapperRef = React.useRef<HTMLDivElement | null>(null)
  const [chartWidth, setChartWidth] = React.useState(0)

  const [menuOpen, setMenuOpen] = React.useState(false)
  const [menuPosition, setMenuPosition] = React.useState({ x: 0, y: 0 })

  const isChecked = selectedIds.includes(violation?.id)

  React.useEffect(() => {
    if (chartWrapperRef.current && isChartExpanded) {
      setChartWidth(chartWrapperRef.current.offsetWidth)
    }
  }, [isChartExpanded, chartActivities])

  const fetchChartData = async (tachographMode = vehicleTachographMode) => {
    if (!companyId || !driverId || !violation?.startTime) return

    const startMoment = moment(violation.startTime).startOf('day')
    const endMoment = startMoment.clone().add(24, 'hours')

    setIsChartLoading(true)
    try {
      const startTime = encodeURIComponent(
        startMoment.format('YYYY-MM-DD HH:mm'),
      )
      const endTime = encodeURIComponent(endMoment.format('YYYY-MM-DD HH:mm'))
      const data = await getDriverActivity({
        companyId,
        driverId,
        startTime,
        endTime,
        includeTrips: true,
        includeVehicleTachographData: tachographMode,
      })
      if (data) {
        setChartActivities(data as ActivityInfo)
      }
    } catch (err) {
      console.error('Error fetching chart activity:', err)
    } finally {
      setIsChartLoading(false)
    }
  }

  const handleOpenNoteModal = async () => {
    setMenuOpen(false)
    if (!chartActivities) {

      await fetchChartData()
    }
    setIsNoteModalOpen(true)
  }


  const buildCertificateDates = (): string[] => {
    if (!violation?.startTime) return []
    const start = moment(violation.startTime).startOf('day')
    const end = moment(violation.endTime || violation.startTime).startOf('day')
    const dates: string[] = []
    let cur = start.clone()
    let safety = 366
    while (cur.isSameOrBefore(end) && safety-- > 0) {
      dates.push(cur.format('YYYY-MM-DD'))
      cur = cur.add(1, 'day')
    }
    return dates
  }

  const handleOpenCertificateModal = () => {
    setMenuOpen(false)
    setIsCertificateModalOpen(true)
  }


  const handleToggleTachographChart = async () => {
    setMenuOpen(false)
    if (vehicleTachographMode) {
      setVehicleTachographMode(false)
      setIsChartExpanded(false)
      return
    }
    setVehicleTachographMode(true)
    setIsChartExpanded(true)
    await fetchChartData(true)
  }

  const openChart = () => {
    if (!isChartExpanded) {
      if (!chartActivities) {
        fetchChartData()
      }
      setIsChartExpanded(true)
    } else {
      setIsChartExpanded(false)
    }
  }

  const navigateWeekly = () => {
    if (!companyId || !driverId || !violation?.startTime) return

    const date = moment(violation.startTime).format('YYYY-MM-DD')
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/weeklyActivity/${date}`,
      { state: { showViolations: true } },
    )
  }

  const navigateMonthly = () => {
    if (!companyId || !driverId || !violation?.startTime) return
    const month = moment(violation.startTime).format('YYYY-MM')
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${driverId}/monthlyActivity/${month}`,
    )
  }

  const handleClick = () => {
    openChart()
  }

  const handleDoubleClick = () => {
    navigateWeekly()
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setMenuPosition({ x: e.clientX, y: e.clientY })
    setMenuOpen(true)
  }

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const hasActivities =
    chartActivities &&
    Array.isArray(chartActivities.activities) &&
    chartActivities.activities.length > 0

  const startMoment = violation?.startTime
    ? moment(violation.startTime).startOf('day')
    : null


  return (
    <>
      <tr
        className={`notches-border--right tw-relative tw-w-fit tw-cursor-pointer tw-border-b hover:tw-bg-primary/10 ${isChecked ? 'selected tw-bg-primary/10' : 'tw-bg-transparent'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      >
        <td
          className="tw-w-4 tw-px-3"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={printed}
            onChange={(e) => onPrintedChange(e.target.checked)}
          />
        </td>
        <TableTextRow
          size="compact"
          textSize="xs"
          className="tw-pl-[26px]"
          icon={
            <FaIcon
              icon={getViolationWeightIcon(violation?.weight)}
              className={`${violation?.weight.includes(ViolationWeightType.NN) && 'tw-text-red-500'} `}
            />
          }
        >
          {violation?.weight}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {t(getViolationName(violation?.type))}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {formatDateWithTime(violation?.startTime)}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {formatDateWithTime(violation?.endTime)}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {formatTimespan(violation?.exceededBy)}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {violation?.chargeSymbol}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {violation?.driverCharge}
        </TableTextRow>
        <TableTextRow size="compact" textSize="xs">
          {violation?.companyCharge}
        </TableTextRow>
      </tr>

      <ControlledMenu
        anchorPoint={menuPosition}
        state={menuOpen ? 'open' : 'closed'}
        onClose={() => setMenuOpen(false)}
        portal={true}
        onContextMenu={(e) => e.preventDefault()}
      >
        <MenuItem
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
          onClick={() => {
            openChart()
            setMenuOpen(false)
          }}
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faTriangleExclamationSolid}
              className="tw-w-3 tw-text-content-base"
            />
            Pokaż naruszenia na wykresie
          </span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuOpen(false)
            handleCalculateViolations()
          }}
          disabled={violationsBusy.busy}
          title={
            violationsBusy.busy
              ? violationsBusy.startedBy
                ? `${violationsBusy.startedBy}: ${t('driverViolations.calculation-in-progress')}`
                : t('driverViolations.calculation-in-progress')
              : undefined
          }
          className="tw-my-0 tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faRefreshSolid}
              className="tw-w-3 tw-text-content-base"
            />
            Przelicz naruszenia
          </span>
        </MenuItem>
        <MenuDivider className="tw-m-0 tw-p-0" />
        <MenuItem
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
          onClick={() => {
            navigateWeekly()
            setMenuOpen(false)
          }}
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faCalendarWeekSolid}
              className="tw-w-3 tw-text-content-base"
            />
            Widok tygodniowy
          </span>
        </MenuItem>
        <MenuItem
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
          onClick={() => {
            navigateMonthly()
            setMenuOpen(false)
          }}
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faCalendarDaysSolid}
              className="tw-w-3 tw-text-content-base"
            />
            Widok miesięczny
          </span>
        </MenuItem>
        <MenuDivider className="tw-m-0 tw-p-0" />
        <MenuItem
          onClick={handleOpenCertificateModal}
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faFileCertificateSolid}
              className="tw-w-3 tw-text-content-base"
            />
            {t('driverCalendarMenuActions.generateAttestation')}
          </span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!driverId) return
            handlePrintInstruction({
              selectedIds: [driverId],
              drivers: [{ id: driverId }],
              companyId,
            })
            setMenuOpen(false)
          }}
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faPrintSolid}
              className="tw-w-3 tw-text-content-base"
            />
            {t('menu.violation-print-statement')}
          </span>
        </MenuItem>
        <MenuItem
          onClick={handleToggleTachographChart}
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faChartBulletSolid}
              className="tw-w-3 tw-text-content-base"
            />
            {vehicleTachographMode
              ? 'Ukryj wykres tachografu'
              : 'Pokaż wykres tachografu'}
          </span>
        </MenuItem>
        <MenuItem
          onClick={handleOpenNoteModal}
          className="tw-my-0 tw-cursor-pointer tw-px-4 tw-py-1.5 tw-text-sm tw-text-content-base"
        >
          <span className="tw-flex tw-items-center tw-gap-2">
            <FaIcon
              icon={faNoteSolid}
              className="tw-w-3 tw-text-content-base"
            />
            {chartActivities?.notes?.[0]?.text
              ? 'Edytuj adnotację'
              : 'Dodaj adnotację'}
          </span>
        </MenuItem>
      </ControlledMenu>

      {isChartExpanded && (
        <tr className="tw-border-b tw-border-base-300">
          <td colSpan={9} className="tw-p-0">
            <div
              ref={chartWrapperRef}
              className="tw-relative tw-min-h-[130px] tw-w-full tw-overflow-visible tw-bg-[#F3F3F3]"
            >
              {isChartLoading && (
                <div className="tw-absolute tw-inset-0 tw-z-[999] tw-grid tw-place-items-center tw-bg-black/10">
                  <LoaderIcon className="tw-animate-spin" />
                </div>
              )}
              {hasActivities && startMoment && chartWidth > 0 && (
                <Timeline
                  {...({
                    startTime: startMoment.clone(),
                    days: 1,
                    width: chartWidth,
                    height: SCENE_HEIGHT,
                    extraCurrentDate: moment(violation.startTime).format(
                      'YYYY-MM-DD',
                    ),
                    translate: t,
                    activities: chartActivities!.activities,
                    cardInserts: chartActivities!.cardInserts || [],
                    places: vehicleTachographMode
                      ? chartActivities!.places || []
                      : (chartActivities!.places || []).filter(
                          (p: any) => !p.isAutomaticBorder,
                        ),
                    dayActivities: chartActivities!.dayActivities || [],
                    violations: chartActivities!.violations || [],
                    dailyRests: chartActivities!.dailyRests || [],
                    dailyPartialRegularRests:
                      chartActivities!.dailyPartialRegularRests || [],
                    weeklyRests: chartActivities!.weeklyRests || [],
                    weeklyReducedRests:
                      chartActivities!.weeklyReducedRests || [],
                    breaks: chartActivities!.breaks || [],
                    partialBreaks: chartActivities!.partialBreaks || [],
                    weekRecompenses: chartActivities!.weekRecompenses || [],
                    outOfScopes: chartActivities!.outOfScopes || [],
                    existingDelegations: [],
                    changeAreas: [],
                    tmsData: [],
                    minimalPaymentCountries: [],
                    periodSelections: [],
                    delegationMarkers: [],
                    allEvents: [],
                    allTripEvents: [],
                    allPreviousTripEvents: [],
                    countries: [],
                    selectedActivity: null,
                    editedActivity: null,
                    isAddingNew: false,
                    simpleMode: true,
                    editMode: false,
                    disableTooltips: false,
                    showViolations: true,
                    canEdit: false,
                    canGenerateAttestation: false,
                    onSelected: () => {},
                    onContextMenuChanged: () => {},
                    onAdd: () => {},
                    onEdit: () => {},
                    onViolationSelected: () => {},
                    onPeriodSelectionsChanged: () => {},
                  } as unknown as TimelineProps)}
                />
              )}
            </div>
          </td>
        </tr>
      )}

      {isNoteModalOpen && violation?.startTime && (
        <NoteModal
          close={() => setIsNoteModalOpen(false)}
          refreshActivities={() => fetchChartData()}
          driverId={driverId}
          companyId={companyId}
          date={moment(violation.startTime)}
          existingNote={chartActivities?.notes?.[0]?.text || ''}
          dayType={chartActivities?.notes?.[0]?.dayType ?? DayType.NONE}
        />
      )}

      {isCertificateModalOpen && violation?.startTime && companyId && (
        <CertificateModal
          onClose={() => setIsCertificateModalOpen(false)}
          companyId={companyId}
          driverId={driverId}
          selectedDates={buildCertificateDates()}
        />
      )}
    </>
  )
}
